"use client";

import logger from "@/utils/logger";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth-context";
import { useNotificationSignalR } from "@/hooks/useNotificationSignalR";
import { useSignalRReconnect } from "@/hooks/useSignalRReconnect";

/**
 * Provider that establishes SignalR connection for real-time trip status updates.
 * This provider listens for TripStatusChanged events and invalidates the trips query
 * to trigger automatic UI refresh when trip statuses change.
 */
export function TripStatusSignalRProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const queryClient = useQueryClient();
    const { userProfile } = useAuth();

    const invalidateSharedTripAndRequestQueries = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: ["trips", "list"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["tripList"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["trips"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["tripDetails"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["trip"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["requests"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["requestList"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["requestViewDetails"],
            refetchType: "active",
        });
        queryClient.invalidateQueries({
            queryKey: ["tripComments"],
            refetchType: "active",
        });
    }, [queryClient]);

    // Handler for trip status changes - invalidates trips query to trigger refresh
    const handleTripStatusChange = useCallback(
        (data: {
            tripId: number;
            newStatus: string;
            oldStatus: string | null;
            changedAt: string;
            changedBy?: number;
        }) => {
            logger.info(`🔔 [DEBUG-WEB] TripStatusChanged event received!`);
            logger.info(
                `🔔 [DEBUG-WEB] Trip ${data.tripId} status changed: ${data.oldStatus} → ${data.newStatus}`
            );
            logger.info(`🔔 [DEBUG-WEB] Full data:`, data);

            logger.info(
                `🔔 [TripStatusSignalR] Trip ${data.tripId} status changed: ${data.oldStatus} → ${data.newStatus}`
            );
            logger.info(`🔔 [TripStatusSignalR] Full data received:`, data);

            // Invalidate trip list queries (all pages and filters)
            logger.info(`♻️ [DEBUG-WEB] Invalidating trip lists cache...`);
            logger.info(
                `♻️ [TripStatusSignalR] Invalidating trip lists cache...`
            );
            invalidateSharedTripAndRequestQueries();

            // Optionally invalidate specific trip details if viewing a single trip
            logger.info(
                `♻️ [DEBUG-WEB] Invalidating tripDetails cache for trip ${data.tripId}...`
            );
            logger.info(
                `♻️ [TripStatusSignalR] Invalidating tripDetails cache for trip ${data.tripId}...`
            );
            queryClient.invalidateQueries({
                queryKey: ["tripDetails", data.tripId],
            });

            logger.info(
                `✅ [DEBUG-WEB] All caches invalidated for trip ${data.tripId}`
            );
            logger.info(
                `✅ [TripStatusSignalR] All caches invalidated for trip ${data.tripId}`
            );
        },
        [invalidateSharedTripAndRequestQueries, queryClient]
    );

    useSignalRReconnect(
        useCallback(() => {
            logger.info(
                "♻️ [TripStatusSignalR] SignalR reconnected, reconciling active trip/request queries"
            );
            invalidateSharedTripAndRequestQueries();
        }, [invalidateSharedTripAndRequestQueries]),
        "TripStatusSignalRReconnectRecovery"
    );

    // Establish SignalR connection when app starts
    // Notifications are handled by NotificationPopover
    useNotificationSignalR(
        "Dispatch IQ",
        userProfile?.email || "",
        () => {
            // No-op: notifications are handled by NotificationPopover
        },
        handleTripStatusChange,
        "DispatchTripStatusChange" // Descriptive handler name
    );

    return <>{children}</>;
}
